import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSelect, useViewport } from '@chilecompra/react-kit'
import { Card, DataTable, Select, Tooltip, Typography } from '@chilecompra/react-kit/components'
import ProtectByFeatures from '../../security/ProtectByFeatures'
import { exportToCsv } from '../../modules/utils/exports'
import generateYearsBetween from '../../modules/utils/years'
import { onGetContingenciesBuyersListThunk } from './ContingenciesBuyersListSection.actions'
import {
  Action,
  ActionCircle,
  ActionItem,
  ActionText,
  Content,
  DownloadIcon,
  ExclamationIcon,
  GridContainer,
  GridContainerFooter,
  GridItem,
  Info,
  InfoCircle,
  InfoText,
  Link,
  Root
} from './ContingenciesBuyersListSection.styles'
import { formatAmount } from '../../modules/utils/formatters'

/**
 * The ContingenciesBuyersListSection's container.
 */
const ContingenciesBuyersListSection = () => {
  const dispatch = useDispatch()
  const { latestUpdate } = useSelector(state => state.topNavbar)

  const [page, setPage] = useState(1)
  const [pages, setPages] = useState(0)

  const [fieldsSort, setFieldsSort] = useState([
    { direction: 'asc', order: 'name', value: 1 },
    { direction: 'asc', order: 'rut', value: 2 },
    { direction: 'asc', order: 'amount', value: 3 },
    { direction: 'asc', order: 'oc', value: 5 }
  ])
  const [sort, setSort] = useState({ direction: 'asc', order: 'name' })

  const { data, loading } = useSelector(state => state.contingenciesBuyersList)
  const {
    size: { isUpToMedium }
  } = useViewport()

  const { contingencyOptions, contingencySelected } = useSelector(state => state.contingencies)
  const [startYear, setStartYear] = useState(undefined)
  const [endYear, setEndYear] = useState(undefined)
  const getYearOptions = contingencyOptions?.find(option => option.value === contingencySelected)
  const yearsOptions = useMemo(() => generateYearsBetween(endYear, startYear), [endYear, startYear])

  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: endYear,
    changeCallback: updatedValue => {
      setPage(1)
      dispatch(
        onGetContingenciesBuyersListThunk({
          year: updatedValue,
          contingencyId: contingencySelected,
          page,
          order: sort.order,
          direction: sort.direction,
          callback: pages => setPages(pages)
        })
      )
    },
    overwriteCallback: () => endYear
  })

  const handleOnChange = (_, page) => setPage(page)
  const handleOnSort = value =>
    setSort(() => {
      const fieldSort = fieldsSort.find(field => field.value === value)
      setFieldsSort(
        fieldsSort.map(field => {
          if (field.value === value)
            return {
              order: fieldSort.order,
              direction: fieldSort.direction === 'asc' ? 'desc' : 'asc',
              value: field.value
            }
          return field
        })
      )
      return { order: fieldSort.order, direction: fieldSort.direction === 'asc' ? 'desc' : 'asc' }
    })

  const handleDownloadCsv = () => {
    const csv = [
      ['Link', 'Nombre de la institución', 'RUT', 'Monto transado', 'Monto transado (USD)', 'N° de órdenes de compra'],
      ...data
    ]
    exportToCsv(csv, 'Listado compras por contingencias')
  }

  useEffect(() => {
    dispatch(
      onGetContingenciesBuyersListThunk({
        year: endYear,
        contingencyId: contingencySelected,
        page,
        order: sort.order,
        direction: sort.direction,
        callback: pages => setPages(pages)
      })
    )
  }, [endYear, page, sort])

  useEffect(() => {
    setStartYear(getYearOptions?.startYear)
    setEndYear(getYearOptions?.endYear)
  }, [getYearOptions])

  return (
    <Root>
      <Content>
        <GridContainer>
          <GridItem xs={12} md={6}>
            <Typography
              color="primary"
              fontSize="18px"
              fontWeight="bold"
              lineHeight="21px"
              margin={isUpToMedium ? '96px 0 0' : '48px 0 24px'}
              variant="body1"
            >
              Proveedores del Estado
            </Typography>
            <Typography
              fontSize="34px"
              fontWeight="bold"
              lineHeight="38px"
              margin={isUpToMedium ? '12px 0' : '0'}
              variant="h2"
              width={isUpToMedium ? '485px' : undefined}
            >
              Compras por COVID-19 a cada proveedor
            </Typography>
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={isUpToMedium ? 5 : 12}>
            <Typography
              fontSize="18px"
              lineHeight="24px"
              margin={isUpToMedium ? '0' : '24px 0'}
              variant="body1"
              width={isUpToMedium ? '448px' : undefined}
            >
              Revisa el listado de los proveedores que han recibido una orden de compra por motivo COVID-19.
            </Typography>
          </GridItem>
        </GridContainer>
        <GridContainer md={2} xs={12} margin="48px 0 0">
          <Select label="AÑO" onChange={handleChangeYear} options={yearsOptions || []} value={yearValue} />
        </GridContainer>
        <Card color="blank" elevation="1">
          <DataTable
            customPagination={{ onChangePage: handleOnChange, records: data, page, pages, onSortColumn: handleOnSort }}
            footer={
              <GridContainer alignItems="center">
                <Typography color="black3" fontSize="12px" italic lineHeight="20px">
                  Los montos transados corresponden a montos brutos (monto neto + impuestos).
                </Typography>
              </GridContainer>
            }
            headers={[
              { label: 'Proveedor', value: 1 },
              { label: 'RUT', value: 2 },
              { label: 'Monto transado', value: 3 },
              { label: 'Monto transado ($USD)', value: 4 },
              { label: 'N° de órdenes de compra', value: 5 }
            ]}
            headersCellsProps={[
              { enableSort: true, width: '35%' },
              { enableSort: true, width: '10%' },
              { enableSort: true, width: '20%' },
              { enableSort: false, width: '20%' },
              { enableSort: true, width: '15%' }
            ]}
            loading={loading}
            records={data}
            recordsDecorators={[
              ([, name, rut]) => <Link to={`/organismos-proveedores/${rut}`}>{name}</Link>,
              ([, , rut]) => rut,
              ([, , , amountCLP]) => `$ ${formatAmount(amountCLP)}`,
              ([, , , , amountUSD]) => `$USD ${formatAmount(amountUSD)}`,
              ([, , , , , quantiyOC]) => quantiyOC
            ]}
          />
        </Card>
        <GridContainerFooter
          alignItems="center"
          justifyContent="space-between"
          margin={isUpToMedium ? '24px 0 128px' : '24px 0 48px'}
          padding="24px"
          spacing={1}
        >
          <GridItem md={5} xs={12}>
            {latestUpdate && (
              <Info>
                <InfoText>Última actualización: {latestUpdate}</InfoText>
                <Tooltip
                  content="Datos actualizados con un mes de desfase ya que se extraen desde el Datawarehouse el cual se procesa y depura de forma mensual."
                  placement="top"
                >
                  <InfoCircle>
                    <ExclamationIcon />
                  </InfoCircle>
                </Tooltip>
              </Info>
            )}
          </GridItem>
          <ProtectByFeatures rule={features => features['contingencies.datatatable']}>
            <GridItem md={2} xs={12}>
              <Action>
                <ActionItem disabled={false} onClick={handleDownloadCsv}>
                  <ActionCircle disabled={false}>
                    <DownloadIcon />
                  </ActionCircle>
                  <ActionText>Descargar datos</ActionText>
                </ActionItem>
              </Action>
            </GridItem>
          </ProtectByFeatures>
        </GridContainerFooter>
      </Content>
    </Root>
  )
}

export default ContingenciesBuyersListSection
